// KDS Layout - Main container for all department screens
import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { 
  ClipboardList, 
  Beaker, 
  Scissors, 
  RefreshCw, 
  Package, 
  Truck,
  Monitor,
  Users,
  Settings,
  Bell,
  AlertTriangle
} from 'lucide-react';
import ProcessingStation from './stations/ProcessingStation';
import StainRemovalStation from './stations/StainRemovalStation';
import AlterationStation from './stations/AlterationStation';
import WashDryStation from './stations/WashDryStation';
import FoldPressStation from './stations/FoldPressStation';
import IssuesStation from './stations/IssuesStation';
import PickupStation from './stations/PickupStation';
import DeliveryStation from './stations/DeliveryStation';
import KDSOverview from './KDSOverview';
import TDSEntry from './TDSEntry';

interface StationInfo {
  id: string;
  name: string;
  icon: React.ReactNode;
  path: string;
  color: string;
  count?: number;
}

const KDSLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [stationCounts, setStationCounts] = useState<Record<string, number>>({});
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [businessData, setBusinessData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const stations: StationInfo[] = [
    {
      id: 'processing',
      name: 'Processing',
      icon: <ClipboardList className="w-5 h-5" />,
      path: '/kds/processing',
      color: '#3B82F6',
      count: stationCounts.processing || 0
    },
    {
      id: 'stain-removal',
      name: 'Stain Removal',
      icon: <Beaker className="w-5 h-5" />,
      path: '/kds/stain-removal',
      color: '#EF4444',
      count: stationCounts.stain_removal || 0
    },
    {
      id: 'alteration',
      name: 'Alterations',
      icon: <Scissors className="w-5 h-5" />,
      path: '/kds/alteration',
      color: '#8B5CF6',
      count: stationCounts.alteration || 0
    },
    {
      id: 'wash-dry',
      name: 'Wash & Dry',
      icon: <RefreshCw className="w-5 h-5" />,
      path: '/kds/wash-dry',
      color: '#10B981',
      count: stationCounts.wash_dry || 0
    },
    {
      id: 'fold-press',
      name: 'Fold & Press',
      icon: <RefreshCw className="w-5 h-5" />,
      path: '/kds/fold-press',
      color: '#F59E0B',
      count: stationCounts.fold_press || 0
    },
    {
      id: 'issues',
      name: 'Issues',
      icon: <AlertTriangle className="w-5 h-5" />,
      path: '/kds/issues',
      color: '#EF4444',
      count: stationCounts.issues || 0
    },
    {
      id: 'pickup',
      name: 'Pickup',
      icon: <Package className="w-5 h-5" />,
      path: '/kds/pickup',
      color: '#06B6D4',
      count: stationCounts.pickup || 0
    },
    {
      id: 'delivery',
      name: 'Delivery',
      icon: <Truck className="w-5 h-5" />,
      path: '/kds/delivery',
      color: '#84CC16',
      count: stationCounts.delivery || 0
    }
  ];

  // Check for existing TDS authentication
  useEffect(() => {
    const checkTDSAuth = () => {
      try {
        const storedBusinessData = localStorage.getItem('kds_business_data');
        const storedTDSCode = localStorage.getItem('kds_tds_code');
        
        if (storedBusinessData && storedTDSCode) {
          const parsedData = JSON.parse(storedBusinessData);
          console.log('🔍 Found existing TDS session:', parsedData.businessName);
          console.log('🏢 Loaded Business ID:', parsedData.businessId);
          setBusinessData(parsedData);
          setIsAuthenticated(true);
        } else {
          console.log('ℹ️ No TDS session found, showing entry screen');
          console.log('📦 Stored business data:', !!storedBusinessData);
          console.log('🏷️ Stored TDS code:', !!storedTDSCode);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('❌ Error loading TDS session:', error);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkTDSAuth();
  }, []);

  // Handle TDS code verification
  const handleCodeVerified = (verifiedBusinessData: any) => {
    console.log('✅ TDS code verified, setting up KDS session:', verifiedBusinessData);
    console.log('🏢 Business ID received:', verifiedBusinessData.businessId);
    setBusinessData(verifiedBusinessData);
    setIsAuthenticated(true);
  };

  // Logout function
  const handleLogout = () => {
    localStorage.removeItem('kds_business_data');
    localStorage.removeItem('kds_tds_code');
    setBusinessData(null);
    setIsAuthenticated(false);
    console.log('🚪 Logged out of TDS session');
  };

  // Fetch station counts periodically (only when authenticated)
  useEffect(() => {
    if (!isAuthenticated || !businessData) return;
    const fetchStationCounts = async () => {
      try {
        // Include business ID in the request for tenant-specific data
        const tdsCode = localStorage.getItem('kds_tds_code');
        const businessId = businessData?.businessId;
        
        if (!businessId) {
          console.log('⚠️ No business ID available for KDS overview');
          return;
        }
        
        console.log('🏢 Fetching KDS overview for business:', businessId);
        let response = await fetch(`/api/kds/overview?business_id=${businessId}&tds_code=${tdsCode}`);
        
        // If unauthorized, try with additional headers
        if (response.status === 401) {
          response = await fetch(`/api/kds/overview?business_id=${businessId}&tds_code=${tdsCode}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token') || 'dev-token'}`,
              'X-TDS-Code': tdsCode || '',
              'X-Business-ID': businessId || ''
            }
          });
        }
        
        if (response.ok) {
          const data = await response.json();
          const counts: Record<string, number> = {};
          
          data.data.stations.forEach((station: any) => {
            counts[station.station] = station.count || 0;
          });
          
          setStationCounts(counts);
          setLastUpdated(new Date());
        }
      } catch (error) {
        console.error('Failed to fetch station counts:', error);
      }
    };

    // Initial fetch
    fetchStationCounts();

    // Set up polling every 20 seconds (reduced frequency)
    const interval = setInterval(fetchStationCounts, 20000);

    return () => clearInterval(interval);
  }, [isAuthenticated, businessData]);

  const getCurrentStation = () => {
    const currentPath = location.pathname;
    return stations.find(station => currentPath.includes(station.id)) || stations[0];
  };

  const currentStation = getCurrentStation();
  const totalOrders = Object.values(stationCounts).reduce((sum, count) => sum + count, 0);

  // Show loading screen
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Monitor className="w-16 h-16 text-blue-600 mx-auto mb-4 animate-pulse" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading SmartDry TDS</h2>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Show TDS entry if not authenticated
  if (!isAuthenticated) {
    return <TDSEntry onCodeVerified={handleCodeVerified} />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Top Navigation Bar */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Monitor className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">SmartDry TDS</h1>
                <p className="text-sm text-gray-500">
                  {businessData ? `${businessData.businessName} - ${businessData.businessType}` : 'Task Display System'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-6">
              {/* Total Orders Counter */}
              <div className="flex items-center space-x-2 bg-blue-50 px-4 py-2 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{totalOrders}</div>
                  <div className="text-xs text-blue-500">Active Orders</div>
                </div>
              </div>
              
              {/* Last Updated */}
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">Last Updated</div>
                <div className="text-xs text-gray-500">
                  {lastUpdated.toLocaleTimeString()}
                </div>
              </div>
              
              {/* Notifications */}
              <button className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg">
                <Bell className="w-6 h-6" />
                {totalOrders > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {totalOrders > 9 ? '9+' : totalOrders}
                  </span>
                )}
              </button>
              
              {/* TDS Code Display */}
              {businessData?.tdsCode && (
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">TDS Code</div>
                  <div className="text-xs text-gray-500 font-mono">
                    {businessData.tdsCode}
                  </div>
                </div>
              )}
              
              {/* Settings */}
              <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg">
                <Settings className="w-6 h-6" />
              </button>
              
              {/* Logout */}
              <button 
                onClick={handleLogout}
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Station Navigation */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-6">
          <div className="flex space-x-1">
            {/* Overview Tab */}
            <button
              onClick={() => navigate('/kds')}
              className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium rounded-t-lg transition-colors ${
                location.pathname === '/kds' 
                  ? 'bg-gray-900 text-white' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <Monitor className="w-4 h-4" />
              <span>Overview</span>
            </button>

            {/* Station Tabs */}
            {stations.map((station) => (
              <button
                key={station.id}
                onClick={() => navigate(station.path)}
                className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium rounded-t-lg transition-colors relative ${
                  currentStation.id === station.id
                    ? 'text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
                style={{
                  backgroundColor: currentStation.id === station.id ? station.color : undefined
                }}
              >
                {station.icon}
                <span>{station.name}</span>
                {(station.count || 0) > 0 && (
                  <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                    currentStation.id === station.id 
                      ? 'bg-white bg-opacity-20 text-white' 
                      : 'bg-red-100 text-red-600'
                  }`}>
                    {station.count || 0}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1">
        <Routes>
          <Route path="/" element={<KDSOverview />} />
          <Route path="/processing" element={<ProcessingStation />} />
          <Route path="/stain-removal" element={<StainRemovalStation />} />
          <Route path="/alteration" element={<AlterationStation />} />
          <Route path="/wash-dry" element={<WashDryStation />} />
          <Route path="/fold-press" element={<FoldPressStation />} />
          <Route path="/issues" element={<IssuesStation />} />
          <Route path="/pickup" element={<PickupStation />} />
          <Route path="/delivery" element={<DeliveryStation />} />
        </Routes>
      </div>

      {/* Status Bar */}
      <div className="bg-gray-800 text-white px-6 py-2">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-4">
            <span>🟢 System Online</span>
            <span>•</span>
            <span>Auto-refresh: 10s</span>
          </div>
          <div className="flex items-center space-x-4">
            <span>Current Station: {currentStation.name}</span>
            <span>•</span>
            <span>{new Date().toLocaleDateString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KDSLayout;
